import React from 'react';
import { MediaRef } from '../../api/payment/brand-deal.api';
import VerifyBadge from '../verify-badge/verify-badge.component';
import { IndividualOrdererBadge } from './orderer-badge';

export interface BrandPostPreviewProps {
  /** Tên hiển thị của creator sẽ đăng bài — khi doanh nghiệp xem trước trước khi chọn creator, để trống. */
  authorName?: string | null;
  authorAvatar?: string | null;
  title?: string;
  /** Caption đã chèn link (dùng renderCaption) — khớp ĐÚNG nội dung bài thật sẽ đăng. */
  captionWithLink: string;
  images: MediaRef[];
  videos: MediaRef[];
  brandName: string;
  /** Hiện dấu xác minh cạnh tên doanh nghiệp — chỉ true khi chắc chắn AdAccount đang Verified
   * (vd hộp lời mời của creator: mời chỉ tạo được từ AdAccount Verified — bất biến phía backend).
   * Mặc định false để form xem trước phía doanh nghiệp (Task 7, chưa chắc đã Verified) tự quyết. */
  brandVerified?: boolean;
  /** Loại người đặt — Individual thì hiện nhãn Cá nhân đã định danh. */
  ordererKind?: string | null;
  className?: string;
}

const initialOf = (name?: string | null) => (name && name.trim() ? name.trim()[0].toUpperCase() : '?');

/**
 * Khung xem trước bài hợp tác quảng cáo — y hệt cách bài sẽ hiện trên tường creator (ảnh/video +
 * caption + nhãn "Được tài trợ bởi {brandName}"). Dùng chung cho trang creator (trước khi
 * Đồng ý lời mời) và form tạo chiến dịch phía doanh nghiệp (Task 7).
 */
const BrandPostPreview: React.FC<BrandPostPreviewProps> = ({
  authorName,
  authorAvatar,
  title,
  captionWithLink,
  images,
  videos,
  brandName,
  brandVerified = false,
  ordererKind,
  className,
}) => {
  const hasVideo = videos.length > 0;

  return (
    <div className={`ui-card overflow-hidden ${className ?? ''}`}>
      <div className="flex items-center gap-3 p-3">
        <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#EAEFFA] text-sm font-bold text-[#0057FF] dark:bg-slate-700 dark:text-blue-300">
          {authorAvatar ? (
            <img src={authorAvatar} alt={authorName ?? ''} className="size-full object-cover" />
          ) : (
            initialOf(authorName)
          )}
        </div>
        <div>
          <div className="ui-heading text-[15px] font-bold">{authorName || 'Creator'}</div>
          <div className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400">
            <span>Được tài trợ bởi {brandName || '...'}</span>
            {brandVerified && <VerifyBadge size={13} />}
            <IndividualOrdererBadge kind={ordererKind} />
          </div>
        </div>
      </div>

      <div className="px-3 pb-3">
        {title && <div className="ui-heading mb-1 text-[15px] font-semibold">{title}</div>}
        <p className="ui-text whitespace-pre-wrap break-words text-[14px]">{captionWithLink}</p>
      </div>

      {hasVideo ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
          src={videos[0].url}
          controls
          playsInline
          className="max-h-[70vh] w-full bg-black object-contain"
        />
      ) : images.length > 0 ? (
        <div className={`grid gap-[2px] ${images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
          {images.slice(0, 4).map((img) => (
            <img key={img.id || img.url} src={img.url} alt="" className="aspect-square w-full object-cover" />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default BrandPostPreview;
